import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Editorial masonry gallery with a lightbox.
 *
 * Props:
 *   photos?: string[]                    // legacy: image src paths only
 *   media?:  Array<{type:'photo'|'video', src:string}>   // mixed media (preferred)
 *   columns?: 2|3|4
 *   gap?: "sm" | "md" | "lg"
 *
 * Photos open in a lightbox with prev/next/Escape.
 * Videos autoplay (muted, looped) inline; they are skipped in the lightbox.
 */
export default function Gallery({ photos = null, media = null, columns = 3, gap = "md" }) {
  // Normalise to a single `items` array of {type, src}.
  const items = useMemo(() => {
    if (Array.isArray(media) && media.length) return media;
    if (Array.isArray(photos) && photos.length)
      return photos.map((src) => ({ type: "photo", src }));
    return [];
  }, [media, photos]);

  // Indices that point to photo items (used for lightbox navigation).
  const photoIndexes = useMemo(
    () => items.map((it, i) => (it.type === "photo" ? i : -1)).filter((i) => i >= 0),
    [items]
  );

  const [active, setActive] = useState(null); // index into `items` (photo only) or null
  const close = useCallback(() => setActive(null), []);

  const stepPhoto = useCallback(
    (dir) => {
      setActive((current) => {
        if (current === null || photoIndexes.length === 0) return current;
        const pos = photoIndexes.indexOf(current);
        if (pos === -1) return current;
        const nextPos = (pos + dir + photoIndexes.length) % photoIndexes.length;
        return photoIndexes[nextPos];
      });
    },
    [photoIndexes]
  );

  const prev = useCallback(() => stepPhoto(-1), [stepPhoto]);
  const next = useCallback(() => stepPhoto(1), [stepPhoto]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, prev, next]);

  // Lock scroll while lightbox is open.
  useEffect(() => {
    if (active !== null) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [active]);

  const colsClass =
    columns === 2
      ? "columns-1 sm:columns-2"
      : columns === 4
      ? "columns-1 sm:columns-2 lg:columns-3 xl:columns-4"
      : "columns-1 sm:columns-2 lg:columns-3";

  const gapClass = gap === "sm" ? "gap-3 md:gap-4" : gap === "lg" ? "gap-6 md:gap-10" : "gap-4 md:gap-6";
  const itemMb = gap === "sm" ? "mb-3 md:mb-4" : gap === "lg" ? "mb-6 md:mb-10" : "mb-4 md:mb-6";

  const activeItem = active !== null ? items[active] : null;
  const activePhotoOrdinal = active !== null ? photoIndexes.indexOf(active) + 1 : 0;

  return (
    <>
      <div className={`${colsClass} ${gapClass} [column-fill:_balance]`} data-testid="gallery-grid">
        {items.map((it, i) => (
          <motion.div
            key={it.src + i}
            initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, delay: (i % 4) * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className={`${itemMb} break-inside-avoid`}
          >
            {it.type === "video" ? (
              <div
                data-testid={`gallery-video-tile-${i}`}
                className="block w-full overflow-hidden bg-espresso/5 relative"
              >
                <video
                  src={it.src}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="block w-full h-auto object-cover"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setActive(i)}
                data-testid={`gallery-tile-${i}`}
                className="block w-full overflow-hidden bg-espresso/5 group relative cursor-zoom-in"
                aria-label={`Open image ${photoIndexes.indexOf(i) + 1} of ${photoIndexes.length}`}
              >
                <img
                  src={it.src}
                  alt={`Salumi Tucson — photograph ${photoIndexes.indexOf(i) + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/15 transition-colors duration-700" />
              </button>
            )}
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {activeItem && activeItem.type === "photo" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100] bg-espresso/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            data-testid="gallery-lightbox"
            onClick={close}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); close(); }}
              data-testid="gallery-close"
              className="absolute top-5 right-5 md:top-8 md:right-8 text-ivory/80 hover:text-ivory transition-colors p-2"
              aria-label="Close gallery"
            >
              <X strokeWidth={1.25} className="size-7" />
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="gallery-prev"
              className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-ivory/70 hover:text-ivory transition-colors p-3"
              aria-label="Previous image"
            >
              <ChevronLeft strokeWidth={1.25} className="size-8 md:size-10" />
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="gallery-next"
              className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-ivory/70 hover:text-ivory transition-colors p-3"
              aria-label="Next image"
            >
              <ChevronRight strokeWidth={1.25} className="size-8 md:size-10" />
            </button>

            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.96, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              src={activeItem.src}
              alt={`Salumi Tucson — enlarged photograph ${activePhotoOrdinal}`}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain shadow-2xl"
            />

            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-ivory/55">
              {activePhotoOrdinal} / {photoIndexes.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
